import { Component, computed, inject, input, signal } from '@angular/core';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { CampoFormularioPublicoResponse } from '../../models/solicitacao-servico.model';
import {
  LIMITE_TEXTO_CURTO,
  LIMITE_TEXTO_LONGO,
  SolicitacaoServicoStore,
  acceptDosFormatos,
  formatarTamanho,
} from '../solicitacao-servico.store';

/**
 * Um campo específico do serviço, desenhado pelo seu tipo. O tipo é INSTRUÇÃO DE RENDERIZAÇÃO
 * vinda do adm — texto curto, texto longo, número, data, seleção ou upload.
 *
 * Lê e escreve direto no store do fluxo, por código do campo: assim a resposta sobrevive à
 * etapa ser destruída e recriada.
 */
@Component({
  selector: 'app-campo-solicitacao',
  templateUrl: './campo-solicitacao.component.html',
  styleUrls: ['./campo-solicitacao.component.css'],
  imports: [LucideModule],
})
export class CampoSolicitacaoComponent {
  protected readonly store = inject(SolicitacaoServicoStore);

  readonly campo = input.required<CampoFormularioPublicoResponse>();

  /** A etapa pede para destacar as pendências (o cidadão tentou avançar). */
  readonly mostrarPendencias = input(false);

  readonly limiteTextoCurto = LIMITE_TEXTO_CURTO;
  readonly limiteTextoLongo = LIMITE_TEXTO_LONGO;

  readonly idCampo = computed(() => `solicitacao-campo-${this.campo().codCampoServicoCps}`);

  readonly idErro = computed(() => `${this.idCampo()}-erro`);

  readonly valor = computed(() => this.store.valoresCampos()[this.campo().codCampoServicoCps] ?? '');

  readonly arquivo = computed(() => this.store.arquivosCampos()[this.campo().codCampoServicoCps] ?? null);

  readonly accept = computed(() => acceptDosFormatos(this.store.formatosCampoUpload()));

  readonly formatosTexto = computed(() => this.store.formatosCampoUpload().join(', '));

  readonly tetoTexto = computed(() => formatarTamanho(this.store.tetoBytes()));

  /** O campo foi mexido — o erro aparece a partir daí, ou quando a etapa pede. */
  private readonly tocado = signal(false);

  /** Recusa do último arquivo escolhido (formato ou tamanho). */
  readonly recusaArquivo = signal('');

  readonly erro = computed(() => {
    if (this.recusaArquivo()) return this.recusaArquivo();

    if (!this.tocado() && !this.mostrarPendencias()) return '';

    return this.store.pendenciasCampos()[this.campo().codCampoServicoCps] ?? '';
  });

  aoDigitar(evento: Event): void {
    const alvo = evento.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    this.store.definirValorCampo(this.campo().codCampoServicoCps, alvo.value);
  }

  aoSair(): void {
    this.tocado.set(true);
  }

  aoEscolherArquivo(evento: Event): void {
    const alvo = evento.target as HTMLInputElement;
    const arquivo = alvo.files?.[0] ?? null;

    this.tocado.set(true);
    this.recusaArquivo.set(this.store.definirArquivoCampo(this.campo().codCampoServicoCps, arquivo) ?? '');

    // Limpa o seletor: escolher o mesmo arquivo de novo precisa disparar o change.
    alvo.value = '';
  }

  removerArquivo(): void {
    this.recusaArquivo.set('');
    this.store.definirArquivoCampo(this.campo().codCampoServicoCps, null);
  }

  formatarTamanho(bytes: number): string {
    return formatarTamanho(bytes);
  }
}
